import type { FC, PropsWithChildren } from 'react'
import Link from 'next/link'
import styled from '@emotion/styled'

import Typography from '@segment/matcha/components/Typography'

import Image from '@segment/matcha/components/Image'

import type { UseContentfulImageOptions } from '@segment/utils/useContentfulImage'
import useContentfulImage from '@segment/utils/useContentfulImage'

interface CustomerCardProps {
  href: string
}

const CustomerCard: FC<PropsWithChildren<CustomerCardProps>> = ({ href, children }) => {
  return (
    <Link href={href}>
      <Wrapper>{children}</Wrapper>
    </Link>
  )
}

export const Content: FC<PropsWithChildren<unknown>> = ({ children }) => (
  <StyledTypography variant="h4">{children}</StyledTypography>
)

const StyledTypography = styled(Typography)`
  margin-top: 10px;
  color: ${({ theme }) => theme.palette.brand.darkAcai[800]};

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    max-width: 440px;
  }
`

const Wrapper = styled.div`
  display: grid;
  gap: 25px;
  grid-template-rows: 30px max-content auto;
  min-height: 360px;
  height: 100%;
  padding: 24px;
  background: ${({ theme }) => theme.palette.neutrals.white[100]};
  box-shadow: ${({ theme }) => theme.shadows[1]};
  border-radius: 10px 10px 64px 10px;
  text-decoration: none;
  transition: box-shadow 0.3s ease;
`

interface LogoProps {
  /**
   * When provided, the logo will render as an img tag. Otherwise it will be a div wrapper for svg component.
   */
  src?: string
  alt?: string
}

const logoImageFormats: UseContentfulImageOptions = {
  ext: `png`,
  height: 30,
  width: 300,
}

export const Logo: FC<PropsWithChildren<LogoProps>> = ({ src, alt = ``, children }) => {
  const [image] = useContentfulImage(src, logoImageFormats) ?? []
  return image ? (
    <LogoImage loader={() => image} src={image} alt={alt} height={30} width={300} />
  ) : (
    <LogoWrapper>{children}</LogoWrapper>
  )
}

const LogoImage = styled(Image)`
  max-height: 30px;
  width: auto;
  height: auto;
  object-position: left bottom;
  object-fit: contain;
  margin: 0;
  height: 100%;
`

const LogoWrapper = styled.div`
  max-height: 30px;
`

export const Footer = styled.div`
  display: flex;
  align-items: flex-end;
`

export default CustomerCard
